import React, { useState } from 'react';
import { Button, Popconfirm, Tooltip } from 'antd';
import { CarOutlined, ContainerOutlined, DeleteOutlined, EditOutlined, TeamOutlined } from '@ant-design/icons';
import { Trans, useTranslation } from 'react-i18next';
import { libellesTraduits } from '../../../i18n/libelles';
import { T, H_TITLE } from './declarationTheme';
import { natureDepuisAdversaire } from '../../../variables/constants';
import AdversaireModal from '../../../components/AdversaireModal';
import { RequiredLegend } from '../../../components/FieldLabel';
import { Panel, PanelAction, EmptyState, PanelFooterNote, ROW_BG, ROW_BORDER } from './Panel';

/* ─────────────────────────────────────────────────────────────────────────
   Étape 4 - Parties adverses

   Une seule chose à faire ici : lister ce qui a été heurté. La saisie
   détaillée vit dans la modale, qui démarre par la nature (véhicule, moto,
   piéton, bâtiment…) et n'affiche ensuite que les champs pertinents.
   ───────────────────────────────────────────────────────────────────────── */
export default function StepPartiesAdverses({ adversaires, setAdversaires }) {
  const { t } = useTranslation('declaration');
  const [modal, setModal] = useState({ open: false, editing: null, index: -1 });

  const ouvrir = (editing = null, index = -1) => setModal({ open: true, editing, index });
  const fermer = () => setModal({ open: false, editing: null, index: -1 });

  const save = (a) => {
    setAdversaires((prev) => {
      if (modal.index >= 0) {
        const next = [...prev]; next[modal.index] = a; return next;
      }
      return [...prev, a];
    });
    fermer();
  };

  const remove = (idx) => setAdversaires((prev) => prev.filter((_, i) => i !== idx));

  /* Bilan humain cumulé côté adverse - affiché en pied de panneau. */
  const bilan = adversaires.reduce((acc, a) => ({
    blesses: acc.blesses + (Number(a.blesses) || 0),
    deces: acc.deces + (Number(a.deces) || 0),
  }), { blesses: 0, deces: 0 });

  return (
    <div style={{ maxWidth: 780 }}>
      <h1 style={H_TITLE}>{t('partiesAdverses.titre')}</h1>

      <Panel
        style={{ marginTop: 26 }}
        icon={<TeamOutlined />}
        title={t('partiesAdverses.impliquees')}
        count={adversaires.length}
        action={
          adversaires.length > 0 && (
            <PanelAction onClick={() => ouvrir()}>{t('commun.ajouter')}</PanelAction>
          )
        }
      >
        {adversaires.length === 0 ? (
          <EmptyState
            icone={<CarOutlined />}
            label={t('partiesAdverses.aucune')}
            actionLabel={t('partiesAdverses.ajouterPremiere')}
            onAction={() => ouvrir()}
          />
        ) : (
          <>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {adversaires.map((a, idx) => (
                <AdversaireCard
                  key={idx}
                  adversaire={a}
                  onEdit={() => ouvrir(a, idx)}
                  onDelete={() => remove(idx)}
                />
              ))}
            </ul>

            {(bilan.blesses > 0 || bilan.deces > 0) && (
              <PanelFooterNote tone={bilan.deces > 0 ? 'danger' : 'warn'}>
                {bilan.deces > 0 ? (
                  <Trans
                    t={t}
                    i18nKey="partiesAdverses.bilanAvecDeces"
                    values={{ blesses: bilan.blesses, deces: bilan.deces }}
                    components={{ 1: <strong />, 3: <strong /> }}
                  />
                ) : (
                  <Trans
                    t={t}
                    i18nKey="partiesAdverses.bilan"
                    values={{ blesses: bilan.blesses }}
                    components={{ 1: <strong /> }}
                  />
                )}
              </PanelFooterNote>
            )}
          </>
        )}
      </Panel>

      <RequiredLegend style={{ marginTop: 16 }} />

      <AdversaireModal
        open={modal.open}
        onClose={fermer}
        onSave={save}
        initial={modal.editing}
      />
    </div>
  );
}

/* Le genre est le seul repère qui distingue une moto d'une voiture dans la
   liste, la nature les regroupant toutes sous « Véhicule ». */
const GENRE_LABELS = libellesTraduits('declaration:partiesAdverses.genres');

/* ══════════ Carte d'une partie adverse ══════════ */
function AdversaireCard({ adversaire: a, onEdit, onDelete }) {
  const { t } = useTranslation('declaration');
  const nature = natureDepuisAdversaire(a);
  const identite = [a.nom, a.prenoms].filter(Boolean).join(' ').trim();
  const titre = identite
    || (a.typeAdversaire === 'AUTRE' ? a.typeAdversaireAutre : null)
    || nature.label;

  const meta = [
    GENRE_LABELS[a.genreVehicule] || null,
    [a.marqueVehicule, a.modeleVehicule].filter(Boolean).join(' ') || null,
    a.couleurVehicule,
    a.assureurAdverse,
  ].filter(Boolean);

  const blesses = Number(a.blesses) || 0;
  const deces = Number(a.deces) || 0;
  const avecRemorque = a.aRemorque || a.genreVehicule === 'REMORQUE';

  return (
    <li
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 13,
        padding: '13px 14px',
        border: `1px solid ${ROW_BORDER}`,
        borderRadius: 11,
        background: ROW_BG,
        transition: 'border-color 140ms ease',
      }}
    >
      <span
        style={{
          flexShrink: 0,
          width: 38, height: 38,
          borderRadius: 10,
          background: T.paper,
          border: `1px solid ${T.line}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 18,
        }}
      >
        {nature.icone}
      </span>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 3 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: T.ink }}>{titre}</span>
          <Chip>{nature.label}</Chip>
          {a.immatriculation && <Chip mono>{a.immatriculation}</Chip>}
          {avecRemorque && (
            <Tooltip title={a.remorqueImmatriculation ? t('partiesAdverses.remorqueImmatriculee', { immatriculation: a.remorqueImmatriculation }) : t('partiesAdverses.avecRemorque')}>
              <span><Chip><ContainerOutlined /> {t('partiesAdverses.remorque')}</Chip></span>
            </Tooltip>
          )}
        </div>

        {meta.length > 0 && (
          <div style={{ fontSize: 12, color: T.inkMuted }}>{meta.join(' · ')}</div>
        )}

        {(blesses > 0 || deces > 0 || a.tauxRc != null) && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
            {blesses > 0 && <Chip tone="warn">{t('partiesAdverses.blesses', { nombre: blesses })}</Chip>}
            {deces > 0 && <Chip tone="danger">{t('partiesAdverses.deces', { nombre: deces })}</Chip>}
            {a.tauxRc != null && <Chip tone="brand">{Number(a.tauxRc) > 0 ? t('partiesAdverses.responsableA', { taux: a.tauxRc }) : t('partiesAdverses.nonResponsable')}</Chip>}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
        <Tooltip title={t('commun.modifier')}>
          <Button type="text" size="small" icon={<EditOutlined />} onClick={onEdit} style={{ color: T.inkMuted }} />
        </Tooltip>
        <Popconfirm
          title={t('partiesAdverses.retirerConfirmation')}
          onConfirm={onDelete}
          okText={t('commun.retirer')}
          cancelText={t('commun.annuler')}
          okButtonProps={{ danger: true }}
        >
          <Button type="text" size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      </div>
    </li>
  );
}

/* Pastille d'information - pas de filet coloré, juste un fond pâle. */
function Chip({ children, tone = 'neutral', mono = false }) {
  const palette = {
    neutral: { bg: T.paper, color: T.inkMuted, line: T.line },
    brand: { bg: T.brandSoft, color: T.brandDark, line: T.brandLine },
    warn: { bg: T.warnSoft, color: T.warn, line: '#fed7aa' },
    danger: { bg: T.dangerSoft, color: T.danger, line: '#fecaca' },
  }[tone];
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        padding: '2px 8px',
        borderRadius: 20,
        background: palette.bg,
        color: palette.color,
        border: `1px solid ${palette.line}`,
        whiteSpace: 'nowrap',
        ...(mono ? { fontFamily: T.fontMono, letterSpacing: '0.03em' } : {}),
      }}
    >
      {children}
    </span>
  );
}
